<template>
  <div>
    <transition name="modal">
      <delete-modal
        v-if="showDeleteModal"
        :loading="loadingDelete"
        @close="closeDeleteModal()"
        @confirmDelete="deleteElement"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="confirmCurrentBudgetUpdate"
        :message="confirmBudgetUpdateMessage"
        @close="confirmCurrentBudgetUpdate = false"
        @confirm="updateCurrentBudget"
      ></confirmation-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="orderCycleToGenerateNewOrders"
        message="¿Estás seguro que deseas generar nuevas órdenes? Estas serán procesadas en cuanto se apruebe la solicitud."
        @close="orderCycleToGenerateNewOrders = null"
        @confirm="requestOrder(orderCycleToGenerateNewOrders)"
      ></confirmation-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="confirmAddressUpdate"
        :message="'¿Estás seguro que deseas actualizar la dirección de entrega?'"
        @close="confirmAddressUpdate = false"
        @confirm="updateAddress"
      ></confirmation-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="confirmRetryPayment"
        :message="'¿Estás seguro que deseas reintentar el pago? Se intentará cobrar al método de pago registrado.'"
        @close="confirmRetryPayment = false"
        @confirm="retryPayment"
      ></confirmation-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="confirmChargeNextCycle"
        :message="chargeNextCycleMessage"
        :loading="retryingPayment"
        @close="confirmChargeNextCycle = false"
        @confirm="chargeNextCycle"
      ></confirmation-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="confirmReactivate"
        :message="'¿Estás seguro que deseas reactivar este plan? El plan volverá a estado activo y se resetearán los contadores de reintentos.'"
        @close="confirmReactivate = false"
        @confirm="confirmReactivateQuote"
      ></confirmation-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="confirmGeneratePendingOrders"
        :message="confirmGeneratePendingOrdersMessage"
        :loading="generatingPendingOrders"
        @close="confirmGeneratePendingOrders = false"
        @confirm="generatePendingOrders"
      ></confirmation-modal>
    </transition>

    <transition name="modal">
      <cash-payment-modal
        v-if="showCashModal && quote"
        :quote="quote"
        :mode="cashModalMode"
        :payment-frequencies="paymentFrequencies || []"
        :delivery-frequencies="deliveryFrequencies || []"
        :loading="recordingCash"
        @close="closeCashModal"
        @confirm="submitCashPayment"
      ></cash-payment-modal>
    </transition>

    <transition name="modal">
      <rejection-modal
        v-if="showCancelPlanModal"
        :loading="cancelingPlan"
        message="Cancelar plan: escribe el motivo de la cancelación. Se cancelarán las órdenes pendientes, se cerrará el ciclo activo y se notificará al cliente."
        @close="showCancelPlanModal = false"
        @confirm="cancelPlan"
      ></rejection-modal>
    </transition>

    <div v-if="!loading">
      <div v-if="quote" class="flex flex-col">
        <div class="card">
          <card-header :title="'Plan #' + quote.id">
            <template #badge>
              <status-badge :status="quote.status" />
            </template>
          </card-header>

          <!-- Courtesy badge shown inline if active -->
          <div v-if="quote.courtesy" class="mt-1">
            <span class="badge badge-warning">
              <span class="badge-dot"></span>
              Plan de cortesía — No se cobra al cliente
            </span>
          </div>

          <!-- State B: active cash plan — amber badge + prepaid-block widget -->
          <div v-if="quote.is_cash_plan && quote.status === 'active'" class="mt-3">
            <span class="badge badge-warning">
              <span class="badge-dot"></span>
              Plan en efectivo
            </span>

            <div class="mt-2 p-3 bg-tertiary-100 border border-tertiary-200 rounded-lg text-sm text-tertiary-700">
              <p class="font-semibold">Bloque prepagado en efectivo</p>
              <p class="mt-1">
                {{ quote.cash_prepaid_cycles }} ciclo{{ quote.cash_prepaid_cycles === 1 ? '' : 's' }}
                · {{ cashCyclesUsed }} usado{{ cashCyclesUsed === 1 ? '' : 's' }}
                / {{ quote.cash_cycles_remaining }} restante{{ quote.cash_cycles_remaining === 1 ? '' : 's' }}
                <span v-if="quote.cash_block_ends_at">
                  · vence {{ formatDate(quote.cash_block_ends_at) }}
                </span>
              </p>
            </div>
          </div>

          <!-- State C: suspended cash plan — amber renewal banner + record-cash action -->
          <div
            v-if="quote.is_cash_plan && quote.status === 'suspended'"
            class="mt-4 p-3 bg-tertiary-100 border border-tertiary-200 rounded-lg"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="font-semibold text-tertiary-700">
                  Renovación en efectivo requerida
                </p>
                <p class="text-sm text-tertiary-600 mt-1">
                  El bloque prepagado se agotó
                  <span v-if="quote.cash_block_ends_at">
                    el {{ formatDate(quote.cash_block_ends_at) }}
                  </span>. Registra el siguiente pago en efectivo para reactivar el plan.
                </p>
              </div>
              <button
                v-if="hasPermission('edit_quotes')"
                class="secondary-button text-xs flex-shrink-0"
                @click="openCashModal('renew')"
                :disabled="recordingCash"
              >
                {{ recordingCash ? 'Procesando...' : 'Registrar pago en efectivo y reactivar' }}
              </button>
            </div>
          </div>

          <!-- State A: active plan (card or courtesy) + cash-eligible client — convert button -->
          <div
            v-if="canConvertToCash"
            class="mt-3"
          >
            <button
              class="secondary-button text-xs"
              @click="openCashModal('activate')"
              :disabled="recordingCash"
            >
              {{ recordingCash ? 'Procesando...' : 'Convertir a plan en efectivo' }}
            </button>
          </div>

          <!-- Admin cancel — any non-canceled plan, incl. "zombie" plans sin dietas activas -->
          <div
            v-if="hasPermission('edit_quotes') && quote.status !== 'canceled'"
            class="mt-3"
          >
            <button
              class="delete-button"
              @click="showCancelPlanModal = true"
              :disabled="cancelingPlan"
            >
              {{ cancelingPlan ? 'Procesando...' : 'Cancelar plan' }}
            </button>
          </div>

          <!-- Re-contact panel — quote issued but not yet activated -->
          <div
            v-if="['pending', 'requested'].includes(quote.status)"
            class="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="font-semibold text-primary-700">Seguimiento / Recontacto</p>
                <p class="text-sm text-content-400 mt-1">
                  Recontactos realizados: <strong>{{ quote.recontact_count || 0 }}</strong>
                  <span v-if="quote.last_recontacted_at">
                    · último {{ formatDate(quote.last_recontacted_at) }}
                  </span>
                </p>
                <p class="text-xs text-content-300 mt-1">
                  Recordatorios automáticos a los 15 y 30 días de la cotización.
                  <span v-if="quote.recontact_15d_sent_at"> · 15d ✓</span>
                  <span v-if="quote.recontact_30d_sent_at"> · 30d ✓</span>
                </p>
              </div>
              <button
                v-if="hasPermission('edit_quotes')"
                class="secondary-button text-xs flex-shrink-0"
                @click="recontact"
                :disabled="recontacting"
              >
                {{ recontacting ? 'Enviando...' : 'Registrar recontacto' }}
              </button>
            </div>
          </div>

          <!-- Pending Diet Change Request Alerts -->
          <div
            v-for="dcr in (quote.pending_diet_change_requests || [])"
            :key="dcr.id"
            class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
          >
            <div class="flex items-start">
              <svg class="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div class="flex-1">
                <p class="font-semibold text-yellow-800">
                  Cambio de dieta pendiente de aprobación del cliente
                </p>
                <p class="text-sm text-yellow-700 mt-1">
                  Se propuso un cambio de dieta para <strong>{{ dcr.current_diet?.pet?.name || 'la mascota' }}</strong>.
                  El cliente tiene hasta el <strong>{{ formatDate(dcr.expires_at) }}</strong> para responder.
                </p>
                <div class="mt-2 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p class="text-yellow-600 font-medium">Dieta actual:</p>
                    <p class="text-yellow-800">
                      {{ dcr.current_diet?.diet_stage?.display_name || 'N/A' }} -
                      {{ dcr.current_diet?.diet_type?.display_name || 'N/A' }}
                    </p>
                    <p class="text-yellow-700">{{ integerToCurrency(dcr.old_weekly_price_formatted) }}/semana</p>
                  </div>
                  <div>
                    <p class="text-yellow-600 font-medium">Nueva dieta:</p>
                    <p class="text-yellow-800">
                      {{ dcr.new_diet?.diet_stage?.display_name || 'N/A' }} -
                      {{ dcr.new_diet?.diet_type?.display_name || 'N/A' }}
                    </p>
                    <p class="text-yellow-700">{{ integerToCurrency(dcr.new_weekly_price_formatted) }}/semana</p>
                  </div>
                </div>
                <p v-if="dcr.reason" class="text-sm text-yellow-700 mt-2">
                  <strong>Razón:</strong> {{ dcr.reason }}
                </p>
                <p class="text-xs text-yellow-600 mt-2">
                  Si el cliente no responde en 7 días, el plan se pausará automáticamente.
                </p>
              </div>
            </div>
          </div>

          <!-- Payment Issues Alert (Stripe plans only — cash plans use the amber widgets above) -->
          <div
            v-if="!quote.is_cash_plan && (quote.payment_retry_count > 0 || quote.status === 'suspended')"
            class="mt-4 p-3 bg-error-50 border border-error-200 rounded-lg"
          >
            <div class="flex items-start justify-between">
              <div>
                <p class="font-semibold text-error-700">
                  Problema de pago detectado
                </p>
                <p class="text-sm text-error-600 mt-1">
                  {{ quote.payment_retry_count }} intento{{ quote.payment_retry_count !== 1 ? 's' : '' }} fallido{{ quote.payment_retry_count !== 1 ? 's' : '' }}
                  <span v-if="quote.payment_retry_date">
                    desde {{ formatDate(quote.payment_retry_date) }}
                  </span>
                </p>
                <p v-if="quote.last_payment_error" class="text-sm text-error-500 mt-1">
                  Ultimo error: {{ quote.last_payment_error }}
                </p>
                <p v-if="quote.suspension_reason" class="text-sm text-error-500 mt-1">
                  Razon de suspension: {{ quote.suspension_reason }}
                </p>
              </div>
              <div class="flex flex-col gap-2">
                <button
                  v-if="quote.status !== 'suspended' && hasPermission('edit_quotes')"
                  class="secondary-button text-xs"
                  @click="openRetryPaymentModal"
                  :disabled="retryingPayment"
                >
                  {{ retryingPayment ? 'Procesando...' : 'Reintentar pago' }}
                </button>
                <button
                  v-if="quote.status === 'suspended' && hasPermission('edit_quotes')"
                  class="secondary-button text-xs"
                  @click="reactivateQuote"
                  :disabled="reactivatingQuote"
                >
                  {{ reactivatingQuote ? 'Procesando...' : 'Reactivar plan' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Charge next cycle: an ACTIVE plan whose charge is due but hasn't
               run. The daily cron pauses silently when the active cycle still has
               unreleased/stuck orders, so there's no failed-attempt to surface it
               in the block above. This lets the admin kick the charge manually. -->
          <div
            v-if="canChargeNextCycle && hasPermission('edit_quotes')"
            class="mt-4 p-3 bg-warning-50 border border-warning-200 rounded-lg"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="font-semibold text-yellow-700">
                  Cobro del siguiente ciclo pendiente
                </p>
                <p class="text-sm text-content-500 mt-1">
                  El cobro venció el {{ formatDate(quote.next_payment_date) }} y aún
                  no se ha procesado. Puedes cobrarlo ahora: se cobra al método
                  registrado (aplicando el saldo a favor si existe) y se genera el
                  siguiente ciclo solo con las mascotas activas.
                </p>
              </div>
              <button
                class="primary-button text-xs flex-shrink-0"
                @click="openChargeNextCycleModal"
                :disabled="retryingPayment"
              >
                {{ retryingPayment ? 'Procesando...' : 'Cobrar siguiente ciclo' }}
              </button>
            </div>
          </div>

          <!-- Section: Informacion General -->
          <section-header title="Informacion General" />

          <div class="detail-grid">
            <detail-field label="Cliente">
              <router-link
                :to="{ name: 'clientsDetail', params: { id: quote.client.id } }"
                class="detail-link"
              >{{ quote.client.name }} {{ quote.client.last_name }}</router-link>
            </detail-field>

            <detail-field label="Fecha de creacion" :value="formatDatetime(quote.created_at)" />

            <detail-field label="Fecha de ultima actualizacion" :value="formatDatetime(quote.updated_at)" />

            <detail-field v-if="quote.status === 'canceled'" label="Fecha de cancelacion" :value="formatDatetime(quote.canceled_at)" />
          </div>

          <!-- Section: Cupon de descuento (solo aplica al primer pago del plan) -->
          <section-header title="Cupón de descuento" />

          <div
            v-if="quote.coupon"
            class="p-3 bg-primary-50 border border-primary-200 rounded-lg"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="font-semibold text-primary-700">
                  {{ quote.coupon.code }}
                </p>
                <p class="text-sm text-content-400 mt-1">
                  {{ couponDiscountLabel }}
                  <span v-if="quote.coupon.first_purchase_only">
                    · Solo primera compra
                  </span>
                </p>
                <p
                  v-if="quote.coupon_redemption"
                  class="text-sm text-success-600 mt-1"
                >
                  Canjeado el {{ formatDate(quote.coupon_redemption.created_at) }}
                  — descuento aplicado:
                  {{ integerToCurrency(quote.coupon_redemption.discount_amount) }}
                </p>
                <p
                  v-else-if="quote.has_successful_plan_payment"
                  class="text-sm text-content-400 mt-1"
                >
                  El plan ya tiene un pago realizado; el cupón no se aplicará.
                </p>
                <p v-else class="text-sm text-content-400 mt-1">
                  Pendiente de canje — el descuento se aplicará en el primer pago.
                </p>
              </div>
              <button
                v-if="canDetachCoupon"
                class="delete-button text-xs flex-shrink-0"
                @click.prevent="detachCoupon"
                :disabled="couponWorking"
              >
                {{ couponWorking ? 'Procesando...' : 'Quitar cupón' }}
              </button>
            </div>
          </div>

          <div v-else class="text-sm">
            <div v-if="canAttachCoupon" class="flex items-center gap-2">
              <input
                type="text"
                v-model="couponCodeInput"
                class="w-48"
                placeholder="Código del cupón"
              />
              <button
                class="secondary-button flex-shrink-0"
                :disabled="couponWorking || !couponCodeInput"
                @click.prevent="attachCoupon"
              >
                {{ couponWorking ? 'Procesando...' : 'Aplicar cupón' }}
              </button>
            </div>
            <p v-else class="text-content-400">
              {{
                quote.has_successful_plan_payment
                  ? 'Este plan ya tiene un pago realizado; los cupones solo aplican al primer pago.'
                  : 'Sin cupón aplicado.'
              }}
            </p>
          </div>

          <div v-if="quote.status === 'pending'"></div>

          <div v-else>
            <!-- Section: Plan y Entrega -->
            <section-header title="Plan y Entrega" />

            <div class="detail-grid">
              <detail-field label="Cantidad total por semana">
                <p class="stat-value">{{ quote.total_quantity }}g</p>
              </detail-field>

              <detail-field v-if="quote.payment_frequency" label="Frecuencia de pago">
                <p class="detail-value">
                  {{ quote.payment_frequency.name }} -
                  {{ quote.payment_frequency.weeks }}
                  {{ quote.payment_frequency.weeks === 1 ? "semana" : "semanas" }}
                </p>
              </detail-field>

              <detail-field label="Metodo de entrega">
                <div class="flex items-center justify-between gap-2">
                  <p class="detail-value">
                    {{
                      quote.current_pickup_delivery
                        ? "Recoger en sucursal"
                        : "Envio"
                    }}
                  </p>
                  <button
                    v-if="hasPermission('edit_quotes') && ['active', 'paused'].includes(quote.status)"
                    type="button"
                    class="text-xs text-primary-600 underline hover:text-primary-800"
                    @click.prevent="openDeliveryModeModal"
                  >
                    Cambiar
                  </button>
                </div>
              </detail-field>

              <detail-field v-if="quote.delivery_frequency" label="Frecuencia de entrega">
                <p class="detail-value">
                  {{ quote.delivery_frequency.name }} -
                  {{ quote.delivery_frequency.weeks }}
                  {{
                    quote.delivery_frequency.weeks === 1 ? "semana" : "semanas"
                  }}
                </p>
              </detail-field>
            </div>

            <!-- Delivery address (complex section, kept as-is but inside Plan y Entrega) -->
            <div v-if="!quote.current_pickup_delivery" class="mt-4 text-sm">
              <p class="detail-label">Direccion de entrega</p>

              <div v-if="showAddressForm" class="flex flex-col mt-2">
                <label for="address_select">Seleccionar dirección</label>
                <select
                  id="address_select"
                  v-model="selectedAddressId"
                  class="w-fit"
                >
                  <option :value="null">Seleccionar dirección</option>
                  <option
                    v-for="address in quote.client.addresses"
                    :key="address.id"
                    :value="address.id"
                  >
                    {{ address.street }} #{{ address.exterior_number }}, {{ address.city }}
                  </option>
                </select>

                <button
                  class="cancel-button mt-2"
                  @click.prevent="showAddressForm = false; selectedAddressId = quote.address_id"
                >
                  Cancelar
                </button>

                <primary-button
                  class="mx-0 mt-2"
                  :loading="loadingAddressUpdate"
                  type="submit"
                  @click.prevent="confirmAddressUpdate = true"
                  >Guardar</primary-button
                >
              </div>

              <div v-else>
                <div v-if="quote.address" class="card-bordered mt-2 w-fit">
                  <p class="text-sm">{{ quote.address.street }} #{{ quote.address.exterior_number }}</p>
                  <p v-if="quote.address.interior_number" class="text-sm">Int. {{ quote.address.interior_number }}</p>
                  <p v-if="quote.address.neighborhood" class="text-sm">{{ quote.address.neighborhood }}</p>
                  <p class="text-sm">{{ quote.address.city }}, {{ quote.address.state.name }}</p>
                  <p class="text-sm">C.P. {{ quote.address.zip_code }}</p>
                </div>
                <p v-else class="text-error-500 font-medium mt-2">
                  No hay direccion asignada
                </p>

                <button
                  v-if="!showAddressForm && hasPermission('edit_quotes') && quote.status !== 'canceled'"
                  class="secondary-button block mt-2"
                  @click.prevent="showAddressForm = true"
                >
                  {{ quote.address ? 'Cambiar' : 'Asignar' }} dirección
                </button>
              </div>
            </div>

            <!-- Section: Facturacion -->
            <section-header title="Facturacion" />

            <div class="detail-grid">
              <detail-field v-if="quote.discount_amount" label="Descuento" :value="integerToCurrency(quote.discount_amount)" />

              <detail-field v-if="quote.total_price" label="Total">
                <p class="stat-value">
                  {{ integerToCurrency(quote.total_price - quote.discount_amount) }}
                </p>
              </detail-field>

              <detail-field v-if="quote.current_payment_method_id" label="Metodo de pago" :value="quote.current_payment_method_id" />

              <detail-field v-if="quote.next_payment_date" label="Fecha del siguiente pago" :value="formatDate(quote.next_payment_date)" />
            </div>

            <!-- Section: Ordenes del periodo actual -->
            <section-header v-if="quote.order_cycles.length > 0" title="Ordenes del periodo actual" />

            <div
              v-for="orderCycle in quote.order_cycles"
              :key="orderCycle.id"
              class="text-sm"
            >
              <div class="flex flex-wrap">
                <div
                  v-for="order in orderCycle.orders"
                  :key="order.id"
                  class="card-bordered mt-2 sm:mr-2"
                >
                  <p class="text-sm">
                    #{{ order.id }} - {{ getStatus(order.status) }}
                  </p>
                </div>
              </div>

              <!-- Section: Ordenes pendientes de generar -->
              <section-header
                v-if="orderCycle.total_cycles - orderCycle.cycle_count > 0"
                title="Ordenes pendientes de generar"
              />

              <div class="flex flex-wrap">
                <div
                  v-for="index in Math.max(0, orderCycle.total_cycles -
                  orderCycle.cycle_count)"
                  :key="index"
                  class="card-bordered mt-2 sm:mr-2"
                >
                  <div
                    v-for="diet in quote.diets.filter(
                      (diet) => diet.status === 'active'
                    )"
                    :key="diet.id"
                    class="mb-2 last:mb-0"
                  >
                    <p class="text-sm font-medium">{{ diet.pet.name }}</p>
                    <p class="text-xs text-content-400">
                      {{ diet.diet_stage?.display_name || 'Sin etapa' }} · {{ diet.diet_type?.display_name || 'Sin tipo' }}
                    </p>
                    <p class="text-xs text-content-400">
                      {{ diet.total_quantity }}g/semana · {{ integerToCurrency(diet.weekly_price) }}/semana
                    </p>
                    <router-link
                      :to="{ name: 'petsDetail', params: { id: diet.pet.id } }"
                      class="text-xs text-primary-500 hover:underline"
                    >
                      Ver dieta
                    </router-link>
                  </div>
                </div>
              </div>

              <div
                v-if="orderCycle.total_cycles - orderCycle.cycle_count > 0"
                class="mt-2 text-sm"
              >
                <p class="detail-label">
                  Fecha de generacion de
                  {{
                    quote.diets.length > 1
                      ? "siguiente set de ordenes"
                      : "siguiente orden"
                  }}
                </p>
                <p class="detail-value">
                  {{ formatDate(orderCycle.next_cycle_date) }}
                </p>

                <button
                  v-if="
                    validateIfCanRequest(orderCycle) &&
                    quote.status !== 'canceled'
                  "
                  class="secondary-button block mt-2"
                  @click.prevent="orderCycleToGenerateNewOrders = orderCycle"
                >
                  Solicitar generar
                  {{
                    quote.diets.length > 1
                      ? "siguiente set de órdenes"
                      : "siguiente orden"
                  }}
                </button>

                <p v-if="hasPendingRequest(orderCycle)" class="mt-2">
                  Ya existe una solicitud pendiente.
                </p>

                <!-- Button for cancelled plans - admin can generate remaining orders directly -->
                <div
                  v-if="
                    quote.status === 'canceled' &&
                    hasPermission('edit_orders') &&
                    orderCycle.total_cycles - orderCycle.cycle_count > 0
                  "
                  class="mt-2"
                >
                  <!-- CAMBIO 2026-04-23: toggle para elegir si las órdenes generadas deducen del saldo.
                       Default true. Destildar solo para reemplazar órdenes ya cobradas perdidas/canceladas
                       o para entregas de cortesía. -->
                  <label class="flex items-center text-xs text-content-300 cursor-pointer mb-2">
                    <input
                      type="checkbox"
                      v-model="generatePendingOrdersDeductBudget"
                      class="mr-2"
                    />
                    <span>
                      Deducir costo del saldo del cliente
                      <span class="text-content-500">(destildar solo para cortesías o reemplazos)</span>
                    </span>
                  </label>

                  <button
                    class="primary-button block mx-0"
                    @click.prevent="openGeneratePendingOrdersModal(orderCycle)"
                  >
                    Generar {{ orderCycle.total_cycles - orderCycle.cycle_count }} orden(es) pendiente(s)
                  </button>
                </div>
              </div>

              <!-- Section: Saldo -->
              <section-header title="Saldo Actual" />

              <div class="text-sm">
                <div v-if="showCurrentBudgetForm" class="flex flex-col space-y-2 max-w-md">
                  <!-- CAMBIO 2026-04-23: formulario expandido con motivo obligatorio -->
                  <label class="text-xs text-content-300 font-medium">Nuevo saldo</label>
                  <input
                    type="number"
                    step="any"
                    v-model="currentBudget"
                    class="w-full"
                    placeholder="Ej. 1000.00"
                  />

                  <label class="text-xs text-content-300 font-medium mt-2">
                    Motivo del cambio <span class="text-error-500">*</span>
                  </label>
                  <textarea
                    v-model="currentBudgetReason"
                    class="w-full"
                    rows="3"
                    placeholder="Ej. Corrección por producto devuelto el 15/04/2026 — cortesía al cliente"
                    maxlength="500"
                  ></textarea>
                  <p class="text-xs text-content-300">
                    {{ currentBudgetReason.length }}/500 caracteres. El motivo queda registrado en el historial del plan.
                  </p>

                  <!-- Alerta visual si el saldo nuevo parece extremo -->
                  <div
                    v-if="currentBudgetWarnings.length > 0"
                    class="p-2 bg-warning-50 border border-warning-200 rounded text-xs text-warning-700"
                  >
                    <p class="font-medium">⚠️ Advertencia:</p>
                    <ul class="list-disc list-inside">
                      <li v-for="w in currentBudgetWarnings" :key="w">{{ w }}</li>
                    </ul>
                    <p class="mt-1">Puedes continuar si es correcto, pero verifica antes de confirmar.</p>
                  </div>

                  <div class="flex space-x-2 mt-2">
                    <button
                      class="cancel-button"
                      @click.prevent="cancelCurrentBudgetEdit"
                    >
                      Cancelar
                    </button>
                    <primary-button
                      class="mx-0"
                      :loading="loadingCurrentBudgetUpdate"
                      :disabled="!currentBudgetReason || currentBudgetReason.length < 5"
                      @click.prevent="confirmCurrentBudgetUpdate = true"
                    >
                      Guardar
                    </primary-button>
                  </div>
                </div>
                <div v-else>
                  <p
                    class="stat-value"
                    :class="[
                      { 'text-success-600': orderCycle.current_budget > 0 },
                      { 'text-error-500': orderCycle.current_budget < 0 },
                    ]"
                  >
                    {{ integerToCurrency(orderCycle.current_budget) }}
                  </p>

                  <button
                    v-if="
                      !showCurrentBudgetForm &&
                      hasPermission('edit_quotes') &&
                      quote.status !== 'canceled'
                    "
                    class="secondary-button block mt-2"
                    @click.prevent="startCurrentBudgetEdit(orderCycle)"
                  >
                    Cambiar saldo actual
                  </button>
                </div>
              </div>
            </div>

            <!-- Section: Pagos -->
            <section-header v-if="quote.order_invoices.length > 0" title="Pagos" />

            <div class="flex flex-wrap">
              <payment
                v-for="orderInvoice in quote.order_invoices"
                :key="orderInvoice.id"
                :orderInvoice="orderInvoice"
                :canRefund="hasPermission('edit_quotes')"
                @refunded="loadQuote()"
                class="sm:mr-4 mt-2"
              >
              </payment>
            </div>

            <div v-if="quote.status === 'canceled'" class="mt-4">
              <detail-field label="Razon de cancelacion" :value="quote.cancel_reason" />
            </div>

            <!-- Section: Historial de intentos de pago -->
            <section-header title="Historial de intentos de pago" />

            <div>
              <div class="flex justify-end mb-2">
                <button
                  v-if="!showPaymentAttempts && paymentAttempts === null"
                  class="secondary-button text-xs"
                  @click="loadPaymentAttempts"
                  :disabled="loadingPaymentAttempts"
                >
                  {{ loadingPaymentAttempts ? 'Cargando...' : 'Ver historial' }}
                </button>
                <button
                  v-else
                  class="secondary-button text-xs"
                  @click="showPaymentAttempts = !showPaymentAttempts"
                >
                  {{ showPaymentAttempts ? 'Ocultar' : 'Mostrar' }}
                </button>
              </div>

              <div v-if="loadingPaymentAttempts" class="flex items-center py-4">
                <loader-icon class="h-6 w-6 text-primary-500 mx-auto"></loader-icon>
              </div>

              <div v-else-if="showPaymentAttempts && paymentAttempts">
                <p v-if="paymentAttempts.length === 0" class="text-sm text-content-400 mt-2">
                  No hay intentos de pago registrados
                </p>

                <table v-else class="table-auto text-sm mt-2 w-full">
                  <thead class="text-left">
                    <tr class="bg-primary-100">
                      <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Fecha</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Tipo</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Monto</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Estado</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Metodo</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Origen</th>
                      <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="attempt in paymentAttempts" :key="attempt.id" class="table-row">
                      <td class="table-cell text-xs">
                        {{ formatDatetime(attempt.created_at) }}
                      </td>
                      <td class="table-cell text-xs">
                        {{ attempt.type_label || attempt.type }}
                      </td>
                      <td class="table-cell">
                        {{ integerToCurrency(attempt.amount) }}
                      </td>
                      <td class="table-cell">
                        <status-badge :status="attempt.status" :label="attempt.status_label" />
                      </td>
                      <td class="table-cell text-xs">
                        <span v-if="attempt.payment_method_brand">
                          {{ attempt.payment_method_brand.toUpperCase() }}
                          <span v-if="attempt.payment_method_last4">
                            *{{ attempt.payment_method_last4 }}
                          </span>
                        </span>
                        <span v-else class="text-content-400">-</span>
                      </td>
                      <td class="table-cell text-xs">
                        {{ attempt.triggered_by_label }}
                        <span v-if="attempt.triggered_by_admin" class="block text-content-400">
                          ({{ attempt.triggered_by_admin.name }})
                        </span>
                      </td>
                      <td class="table-cell text-xs max-w-xs">
                        <span v-if="attempt.status === 'failed'" class="text-error-500">
                          {{ attempt.error_message_translated || attempt.error_message || 'Error desconocido' }}
                        </span>
                        <span v-else-if="attempt.stripe_payment_intent_id" class="text-content-400">
                          {{ attempt.stripe_payment_intent_id }}
                        </span>
                        <span v-else class="text-content-400">-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Section: Historial de cambios -->
          <section-header title="Historial de cambios" />

          <div class="table-wrapper">
            <table class="table-auto text-sm mt-2 w-full">
              <thead class="text-left">
                <tr class="bg-primary-100">
                  <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Campo</th>
                  <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Valor anterior</th>
                  <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Fecha</th>
                  <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Admin</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="history in quote.quote_histories" :key="history.id" class="table-row">
                  <td v-if="history.event" class="table-cell" colspan="2">
                    {{ history.event }}
                  </td>
                  <template v-else>
                    <td class="table-cell">
                      {{ history.field }}
                    </td>
                    <td class="table-cell">
                      {{ integerToCurrency(history.previous_value) }}
                    </td>
                  </template>
                  <td class="table-cell">{{ formatDatetime(history.created_at) }}</td>
                  <td class="table-cell">
                    <template v-if="history.user && history.user.admin">
                      {{ history.user.admin.name + " " + history.user.admin.last_name }}
                    </template>
                    <span v-else class="text-content-400">Sistema</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Section: Dietas -->
        <section-header title="Dietas" />

        <div
          v-for="diet in quote.diets.filter((diet) => diet.status === 'active')"
          :key="diet.id"
          class="card-bordered mb-4"
        >
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="detail-label">Mascota</p>
              <status-badge :status="diet.status" />
              <status-badge
                v-if="diet.pet && diet.pet.deleted_at"
                status="canceled"
                label="Mascota eliminada"
              />
              <status-badge
                v-else-if="diet.pet && diet.pet.status === 'paused'"
                status="paused"
                label="Mascota pausada"
              />
            </div>
            <router-link
              :to="{
                name: 'petsDetail',
                params: { id: diet.pet.id },
              }"
              class="detail-link text-xs"
            >Ver {{ diet.pet.name }}</router-link>
          </div>

          <diet
            :diet="diet"
            :canChangeDiet="true"
            @dietChanged="loadQuote"
          />

          <!-- Catch-up order: this pet is back in the plan but is missing its order
               for the current period (its order was canceled when it was excluded).
               Re-creates that single order, paid from the balance that was returned
               to the plan on exclusion — no card charge, no effect on other pets. -->
          <div
            v-if="
              hasPermission('edit_orders') &&
              quote.status !== 'canceled' &&
              dietMissingOrderInActiveCycle(diet)
            "
            class="mt-3 p-3 rounded-lg bg-warning-50 border border-warning-200"
          >
            <p class="text-xs text-content-500 mb-2">
              Esta mascota está en el plan pero <strong>no tiene orden en el periodo
              actual</strong> (p. ej. se separó del envío, o quedó sin orden tras un
              cambio de frecuencia). Puedes generar la orden que le falta; se paga con
              el saldo disponible del plan, <strong>sin cobrar tarjeta</strong>.
            </p>
            <button
              class="primary-button text-xs block mx-0"
              @click.prevent="openCatchUpModal(diet)"
            >
              Generar la orden faltante de {{ diet.pet && diet.pet.name }}
              ({{ integerToCurrency(catchUpCost(diet)) }})
            </button>
          </div>

          <!-- Separate this pet from the plan's shipments (detach + cancel its
               pending order, credit its cost back to the budget). -->
          <div
            v-if="hasPermission('edit_diets') && quote.status !== 'canceled'"
            class="mt-3 flex justify-end"
          >
            <button
              class="delete-button text-xs"
              @click.prevent="openExcludeDietModal(diet)"
            >
              Separar del envío / Excluir mascota
            </button>
          </div>
        </div>

        <!-- Excluded pets (re-includable) -->
        <div
          v-for="diet in quote.diets.filter((diet) => diet.status === 'excluded')"
          :key="'excluded-' + diet.id"
          class="card-bordered mb-4 border-dashed bg-gray-50"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-2 flex-wrap">
                <p class="text-sm font-medium text-content-500">
                  {{ diet.pet && diet.pet.name }}
                </p>
                <status-badge :status="diet.status" />
                <status-badge
                  v-if="diet.pet && diet.pet.deleted_at"
                  status="canceled"
                  label="Mascota eliminada"
                />
              </div>
              <p class="text-xs text-content-300 mt-0.5">
                Esta mascota no recibe órdenes mientras esté excluida.
              </p>
            </div>
            <div
              v-if="hasPermission('edit_diets')"
              class="flex flex-col gap-2 items-stretch flex-shrink-0"
            >
              <button
                class="delete-button text-xs"
                @click.prevent="separatePendingOrders(diet)"
              >
                Separar del envío
              </button>
              <button
                class="secondary-button text-xs"
                @click.prevent="includeDiet(diet)"
              >
                Incluir
              </button>
            </div>
          </div>
        </div>

        <transition name="modal">
          <confirmation-modal
            v-if="showExcludeDietModal"
            :message="`¿Separar a ${(dietToExclude && dietToExclude.pet && dietToExclude.pet.name) || 'esta mascota'} del plan? Sus órdenes pendientes se separarán del envío y se cancelarán, y su saldo se devolverá al plan. Las demás mascotas no se ven afectadas.`"
            @close="closeExcludeDietModal"
            @confirm="confirmExcludeDiet"
          ></confirmation-modal>
        </transition>

        <transition name="modal">
          <confirmation-modal
            v-if="showCatchUpModal"
            :message="catchUpModalMessage"
            @close="closeCatchUpModal"
            @confirm="confirmCatchUp"
          ></confirmation-modal>
        </transition>

        <!-- Section: Cortesia -->
        <div v-if="quote.status !== 'canceled'" class="mt-6">
          <section-header title="Cortesía" />

          <div
            class="p-4 rounded-2xl border-2"
            :class="quote.courtesy
              ? 'bg-tertiary-100/50 border-tertiary-300'
              : 'bg-white border-primary-100'"
          >
            <div v-if="quote.courtesy" class="flex items-start gap-3">
              <svg class="w-6 h-6 text-tertiary-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div class="flex-1">
                <p class="font-display font-semibold text-tertiary-800">Este plan es de cortesía</p>
                <p class="text-sm text-tertiary-700 mt-1">No se realizan cobros al cliente. Las ordenes se generan sin cargo.</p>

                <!-- Courtesy Plan Configuration -->
                <div v-if="hasPermission('edit_quotes')" class="mt-4">
                  <!-- Activate pending courtesy plan -->
                  <div v-if="quote.status === 'pending'">
                    <div class="mt-2">
                      <label class="text-sm font-medium text-tertiary-700">Frecuencia de entrega</label>
                      <select
                        v-model="courtesyForm.delivery_frequency_id"
                        class="w-full mt-1"
                      >
                        <option :value="null">Seleccionar frecuencia</option>
                        <option
                          v-for="freq in deliveryFrequencies"
                          :key="freq.id"
                          :value="freq.id"
                        >
                          {{ freq.name }} - {{ freq.weeks }} {{ freq.weeks === 1 ? 'semana' : 'semanas' }}
                        </option>
                      </select>
                    </div>

                    <div class="flex items-center mt-3">
                      <input
                        id="courtesy_delivery_enabled"
                        type="checkbox"
                        class="h-5 w-5"
                        v-model="courtesyForm.delivery_enabled"
                      />
                      <span class="mx-2 text-sm text-tertiary-700">Generar entregas</span>
                    </div>

                    <primary-button
                      class="mx-0 mt-3"
                      :loading="activatingCourtesy"
                      @click.prevent="activateCourtesyPlan"
                    >
                      Activar plan de cortesía
                    </primary-button>
                  </div>

                  <!-- Edit active courtesy plan settings -->
                  <div v-else>
                    <div class="detail-grid mt-2">
                      <detail-field label="Entregas">
                        <p class="detail-value font-semibold">{{ quote.delivery_enabled ? 'Activadas' : 'Desactivadas' }}</p>
                      </detail-field>
                      <detail-field v-if="quote.delivery_frequency" label="Frecuencia">
                        <p class="detail-value font-semibold">{{ quote.delivery_frequency.name }}</p>
                      </detail-field>
                    </div>

                    <button
                      v-if="!showCourtesySettingsForm"
                      class="secondary-button text-xs mt-3"
                      @click.prevent="openCourtesySettingsForm"
                    >
                      Modificar configuracion
                    </button>

                    <div v-if="showCourtesySettingsForm" class="mt-3 p-3 bg-white rounded-xl border border-tertiary-200">
                      <div>
                        <label class="text-sm font-medium text-tertiary-700">Frecuencia de entrega</label>
                        <select
                          v-model="courtesyForm.delivery_frequency_id"
                          class="w-full mt-1"
                        >
                          <option
                            v-for="freq in deliveryFrequencies"
                            :key="freq.id"
                            :value="freq.id"
                          >
                            {{ freq.name }} - {{ freq.weeks }} {{ freq.weeks === 1 ? 'semana' : 'semanas' }}
                          </option>
                        </select>
                      </div>

                      <div class="flex items-center mt-3">
                        <input
                          id="courtesy_delivery_toggle"
                          type="checkbox"
                          class="h-5 w-5"
                          v-model="courtesyForm.delivery_enabled"
                        />
                        <span class="mx-2 text-sm text-tertiary-700">Generar entregas</span>
                      </div>

                      <div class="flex gap-2 mt-3">
                        <primary-button
                          class="mx-0"
                          :loading="updatingCourtesySettings"
                          @click.prevent="updateCourtesySettings"
                        >
                          Guardar
                        </primary-button>
                        <button
                          class="cancel-button"
                          @click.prevent="showCourtesySettingsForm = false"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="div-line"></div>

                  <button
                    class="delete-button"
                    @click.prevent="confirmCourtesyToggle = true"
                    :disabled="togglingCourtesy"
                  >
                    {{ togglingCourtesy ? 'Procesando...' : 'Quitar cortesía' }}
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="flex items-center justify-between">
              <div>
                <p class="text-sm text-content-400">Este plan no es de cortesía. Se cobra normalmente al cliente.</p>
              </div>
              <button
                v-if="hasPermission('edit_quotes')"
                class="secondary-button text-xs flex-shrink-0"
                @click.prevent="confirmCourtesyToggle = true"
                :disabled="togglingCourtesy"
              >
                {{ togglingCourtesy ? 'Procesando...' : 'Marcar como cortesía' }}
              </button>
            </div>
          </div>

          <transition name="modal">
            <confirmation-modal
              v-if="confirmCourtesyToggle"
              :message="quote.courtesy
                ? '¿Estás seguro que deseas quitar la cortesía? Se comenzará a cobrar al cliente.'
                : '¿Estás seguro que deseas marcar como cortesía? No se cobrará al cliente.'"
              @close="confirmCourtesyToggle = false"
              @confirm="toggleCourtesy"
            ></confirmation-modal>
          </transition>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>

    <!-- Cambiar método de entrega modal -->
    <div
      v-if="showDeliveryModeModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
      @click.self="closeDeliveryModeModal"
    >
      <div class="bg-white rounded-lg shadow-xl p-6 max-w-md w-11/12">
        <h3 class="text-lg font-semibold mb-4">Cambiar método de entrega</h3>
        <p class="text-sm text-gray-600 mb-4">
          Aplica al plan en general. Los pedidos ya preparados no cambian; los pedidos pendientes o en proceso del ciclo actual sí se actualizan.
        </p>
        <div class="space-y-3">
          <label class="flex items-center space-x-2 cursor-pointer">
            <input type="radio" value="true" v-model="deliveryModeForm.pickupChoice" />
            <span class="text-sm">Recoger en sucursal (Pick-Up)</span>
          </label>
          <label class="flex items-center space-x-2 cursor-pointer">
            <input type="radio" value="false" v-model="deliveryModeForm.pickupChoice" />
            <span class="text-sm">Envío a domicilio</span>
          </label>
        </div>
        <div v-if="deliveryModeForm.pickupChoice === 'false'" class="mt-4">
          <label class="block text-sm font-medium mb-1">Dirección de envío</label>
          <select v-model="deliveryModeForm.addressId" class="w-full p-2 border rounded">
            <option :value="null" disabled>Selecciona una dirección</option>
            <option
              v-for="addr in (quote && quote.client ? quote.client.addresses : [])"
              :key="addr.id"
              :value="addr.id"
            >
              {{ addr.street }} #{{ addr.exterior_number }}, {{ addr.city }}
            </option>
          </select>
          <p v-if="!quote || !quote.client || !quote.client.addresses || quote.client.addresses.length === 0" class="text-xs text-red-600 mt-1">
            El cliente no tiene direcciones registradas.
          </p>
        </div>
        <div v-if="deliveryModeError" class="mt-3 text-sm text-red-600">
          {{ deliveryModeError }}
        </div>
        <div class="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            class="secondary-button"
            :disabled="changingDeliveryMode"
            @click.prevent="closeDeliveryModeModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="primary-button"
            :disabled="changingDeliveryMode || !canSubmitDeliveryMode"
            @click.prevent="submitDeliveryModeChange"
          >
            {{ changingDeliveryMode ? "Guardando..." : "Guardar" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Diet from "@/components/models/Diet";
import Payment from "@/components/models/Payment";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import RejectionModal from "@/components/ui/RejectionModal";
import CashPaymentModal from "@/components/modals/CashPaymentModal";
import api from "@/api";
import deleteMixin from "@/mixins/deleteMixin";
import validationErrors from "@/mixins/validationErrors";
import { formatDate, formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Quote Detail",

  components: {
    Diet,
    Payment,
    ConfirmationModal,
    RejectionModal,
    CashPaymentModal,
  },

  data() {
    return {
      deliveryFrequencies: null,
      paymentFrequencies: null,
      quote: null,
      loading: false,
      loadingDelete: false,
      recontacting: false,
      currentBudget: null,
      // CAMBIO 2026-04-23: motivo obligatorio + seguimiento del ciclo específico que se edita.
      currentBudgetReason: '',
      currentBudgetCycleId: null, // ID del ciclo que se está editando (antes siempre order_cycles[0])
      showCurrentBudgetForm: false,
      confirmCurrentBudgetUpdate: false,
      loadingCurrentBudgetUpdate: false,
      orderCycleToGenerateNewOrders: null,
      showAddressForm: false,
      selectedAddressId: null,
      confirmAddressUpdate: false,
      loadingAddressUpdate: false,
      // Delivery-mode change modal
      showDeliveryModeModal: false,
      deliveryModeForm: { pickupChoice: 'true', addressId: null },
      changingDeliveryMode: false,
      deliveryModeError: null,
      // Payment attempts
      paymentAttempts: null,
      loadingPaymentAttempts: false,
      showPaymentAttempts: false,
      confirmRetryPayment: false,
      retryingPayment: false,
      confirmChargeNextCycle: false,
      confirmReactivate: false,
      reactivatingQuote: false,
      // Courtesy toggle
      confirmCourtesyToggle: false,
      togglingCourtesy: false,
      // Courtesy plan settings
      activatingCourtesy: false,
      showCourtesySettingsForm: false,
      updatingCourtesySettings: false,
      courtesyForm: {
        delivery_frequency_id: null,
        delivery_enabled: true,
      },
      // Admin cancel-plan modal
      showCancelPlanModal: false,
      cancelingPlan: false,
      // Cash payment modal (convert / renew)
      showCashModal: false,
      cashModalMode: "activate",
      recordingCash: false,
      // Coupon attach/detach
      couponCodeInput: "",
      couponWorking: false,
      // Exclude/separate a pet from the plan
      showExcludeDietModal: false,
      dietToExclude: null,
      // Catch-up (reposición) order for a re-included pet missing its current order
      showCatchUpModal: false,
      dietToCatchUp: null,
      // Generate pending orders for cancelled plans
      confirmGeneratePendingOrders: false,
      generatingPendingOrders: false,
      orderCycleToGenerate: null,
      // CAMBIO 2026-04-23: toggle para deducir del saldo al generar órdenes pendientes.
      // Default true (comportamiento correcto). Destildar solo si las órdenes a generar
      // ya fueron cobradas y se están reemplazando/completando (ej. plan cancelado con
      // dinero prepagado que ya se contabilizó en una orden perdida).
      generatePendingOrdersDeductBudget: true,
    };
  },

  mixins: [deleteMixin, validationErrors],

  computed: {
    canSubmitDeliveryMode() {
      if (this.deliveryModeForm.pickupChoice === 'true') return true;
      return !!this.deliveryModeForm.addressId;
    },

    confirmGeneratePendingOrdersMessage() {
      if (!this.orderCycleToGenerate) return '';
      const count = this.orderCycleToGenerate.total_cycles - this.orderCycleToGenerate.cycle_count;
      // CAMBIO 2026-04-23: mensaje incluye si se deducirá del saldo o no.
      const budgetNote = this.generatePendingOrdersDeductBudget
        ? ' Se deducirá el costo de estas órdenes del saldo del cliente.'
        : ' NOTA: NO se deducirá del saldo (cortesía/reemplazo).';
      return `¿Estás seguro que deseas generar ${count} orden(es) pendiente(s)? Las órdenes se crearán en estatus pendiente sin enviar notificaciones al cliente.${budgetNote}`;
    },

    // Show "Cobrar siguiente ciclo" only when an ACTIVE plan's charge is due but
    // hasn't run yet. Failed/suspended plans use the "Reintentar pago" block, so
    // this is gated to payment_retry_count === 0 to avoid two buttons at once.
    canChargeNextCycle() {
      const q = this.quote;
      if (!q || q.is_cash_plan || q.courtesy) return false;
      if (q.status !== "active") return false;
      if (!q.current_payment_method_id) return false;
      if (q.payment_retry_count > 0) return false;
      if (!q.next_payment_date) return false;
      return new Date(q.next_payment_date) <= new Date();
    },

    chargeNextCycleMessage() {
      return (
        "¿Cobrar el siguiente ciclo del plan ahora? Se cobrará al método de pago " +
        "registrado (aplicando el saldo a favor si existe) y se generará el siguiente " +
        "ciclo solo con las mascotas activas. Si el ciclo actual todavía tiene órdenes " +
        "sin entregar, el cobro se detiene y te avisa en vez de cobrar."
      );
    },

    catchUpModalMessage() {
      const diet = this.dietToCatchUp;
      if (!diet) return '';
      const cycle = this.activeOrderCycle();
      const weeks = this.catchUpWeeks();
      const cost = this.catchUpCost(diet);
      const before = cycle ? Number(cycle.current_budget) : 0;
      const after = Math.max(0, before - cost);
      const petName = (diet.pet && diet.pet.name) || 'esta mascota';
      return (
        `¿Generar la orden que le falta a ${petName} en el periodo actual? ` +
        `Se creará 1 orden de ${weeks} semana(s) por ${this.integerToCurrency(cost)}, ` +
        `que se descontará del saldo del plan (saldo: ${this.integerToCurrency(before)} → ${this.integerToCurrency(after)}). ` +
        `NO se cobrará a ninguna tarjeta. Las demás mascotas no se ven afectadas.`
      );
    },

    /**
     * CAMBIO 2026-04-23: mensaje de confirmación dinámico que muestra delta y advertencias.
     */
    confirmBudgetUpdateMessage() {
      if (this.currentBudget === null || this.currentBudget === '') {
        return '¿Estás seguro que deseas actualizar el saldo actual?';
      }

      const cycle = this.quote?.order_cycles?.find(c => c.id === this.currentBudgetCycleId)
                 || this.quote?.order_cycles?.[0];
      const previousBudget = cycle ? Number(cycle.current_budget) : 0;
      const newBudget = Number(this.currentBudget);
      const delta = newBudget - previousBudget;
      const sign = delta >= 0 ? '+' : '';
      const reasonPreview = this.currentBudgetReason
        ? `\n\nMotivo: "${this.currentBudgetReason}"`
        : '';
      const warningText = this.currentBudgetWarnings.length > 0
        ? `\n\n⚠️ Advertencias:\n${this.currentBudgetWarnings.map(w => '• ' + w).join('\n')}`
        : '';

      return `Cambiarás el saldo de $${previousBudget.toFixed(2)} a $${newBudget.toFixed(2)} (${sign}$${delta.toFixed(2)}).` +
             `\n\nEsto NOTIFICARÁ al cliente y registrará el cambio en el historial.` +
             reasonPreview +
             warningText +
             '\n\n¿Confirmar?';
    },

    /**
     * CAMBIO 2026-04-23: advertencias en tiempo real cuando el admin escribe un saldo nuevo.
     * Alerta al admin antes de confirmar para cambios extremos.
     */
    currentBudgetWarnings() {
      if (this.currentBudget === null || this.currentBudget === '') return [];

      const newBudget = Number(this.currentBudget);
      if (isNaN(newBudget)) return [];

      const warnings = [];
      const cycle = this.quote?.order_cycles?.find(c => c.id === this.currentBudgetCycleId)
                 || this.quote?.order_cycles?.[0];
      const totalPrice = cycle ? Number(cycle.total_price) : 0;
      const previousBudget = cycle ? Number(cycle.current_budget) : 0;
      const delta = newBudget - previousBudget;

      if (newBudget < 0) {
        warnings.push('Saldo negativo: forzará cobro extra al cliente en el próximo ciclo.');
      }
      if (totalPrice > 0 && newBudget > totalPrice * 2) {
        warnings.push(`Saldo superior al doble del total del plan ($${newBudget.toFixed(2)} vs $${totalPrice.toFixed(2)}).`);
      }
      if (totalPrice > 0 && Math.abs(delta) > totalPrice) {
        warnings.push(`Cambio de $${Math.abs(delta).toFixed(2)} excede el total del plan.`);
      }
      return warnings;
    },

    // State A — an active plan whose client is cash-eligible can be converted to
    // cash. This also covers graduating a COURTESY (free) plan into a paying cash
    // plan: the modal forces a real payment frequency and the backend clears the
    // courtesy flag on conversion.
    canConvertToCash() {
      return (
        this.quote &&
        this.quote.status === "active" &&
        !this.quote.is_cash_plan &&
        this.quote.client?.allows_cash_payment === true &&
        this.hasPermission("edit_quotes")
      );
    },

    couponDiscountLabel() {
      const coupon = this.quote?.coupon;
      if (!coupon) return "";
      return coupon.discount_type === "percentage"
        ? `${Number(coupon.discount_value)}% de descuento`
        : `${this.integerToCurrency(coupon.discount_value)} de descuento`;
    },

    // Attach/detach only make sense before the first successful plan payment —
    // afterwards the coupon (if any) is a historical record.
    canAttachCoupon() {
      return (
        this.hasPermission("edit_quotes") &&
        this.quote.status !== "canceled" &&
        !this.quote.has_successful_plan_payment
      );
    },

    canDetachCoupon() {
      return (
        this.canAttachCoupon &&
        !this.quote.coupon_redemption
      );
    },

    // Cycles consumed in the current prepaid block.
    cashCyclesUsed() {
      if (!this.quote) return 0;
      const prepaid = Number(this.quote.cash_prepaid_cycles || 0);
      const remaining = Number(this.quote.cash_cycles_remaining || 0);
      return Math.max(0, prepaid - remaining);
    },
  },

  created() {
    this.$store.commit("setSelectedTab", "quotes");

    this.loadFrequencies();
    this.loadQuote();
  },

  methods: {
    formatDate,

    async recontact() {
      if (this.recontacting) return;
      this.recontacting = true;

      try {
        const res = await api.quotes.recontact(this.quote.id);
        this.$store.dispatch("successSnack", res.message || "Recontacto registrado.");
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "No se pudo registrar el recontacto.");
      }

      this.recontacting = false;
    },

    async cancelPlan(reason) {
      const motivo = (reason || "").trim();
      if (!motivo) {
        this.$store.dispatch("errorSnack", "Debes indicar el motivo de la cancelación.");
        return;
      }

      this.cancelingPlan = true;

      try {
        const res = await api.quotes.cancelPlan(this.quote.id, { cancel_reason: motivo });
        this.$store.dispatch("successSnack", res.message || "Plan cancelado exitosamente.");
        this.showCancelPlanModal = false;
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "No se pudo cancelar el plan.");
      }

      this.cancelingPlan = false;
    },

    openExcludeDietModal(diet) {
      this.dietToExclude = diet;
      this.showExcludeDietModal = true;
    },

    closeExcludeDietModal() {
      this.showExcludeDietModal = false;
      this.dietToExclude = null;
    },

    async confirmExcludeDiet() {
      const diet = this.dietToExclude;
      this.showExcludeDietModal = false;
      if (!diet) return;

      try {
        const res = await api.quotes.updateDietStatus(this.quote.id, diet.id, "excluded");
        this.$store.dispatch("successSnack", res.message || "Mascota separada del plan.");
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "No se pudo separar la mascota.");
      } finally {
        this.dietToExclude = null;
      }
    },

    async includeDiet(diet) {
      try {
        const res = await api.quotes.updateDietStatus(this.quote.id, diet.id, "active");
        this.$store.dispatch("successSnack", res.message || "Mascota incluida nuevamente en el plan.");
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "No se pudo incluir la mascota.");
      }
    },

    // Re-runs the reconciliation on an already-excluded pet to detach (and
    // cancel/credit any pending) stuck orders from the shipment group.
    async separatePendingOrders(diet) {
      try {
        const res = await api.quotes.updateDietStatus(this.quote.id, diet.id, "excluded");
        const r = (res && res.reconciled) || {};
        const detached = r.detached || 0;
        const canceled = r.canceled || 0;

        // Report BOTH counts: a stuck pending order that isn't grouped gets
        // canceled (canceled=1) with detached=0 — reporting only `detached` here
        // was falsely telling the admin "no había órdenes" when it did cancel one.
        let message;
        if (detached > 0 || canceled > 0) {
          const parts = [];
          if (detached > 0) parts.push(`${detached} separada(s) del envío`);
          if (canceled > 0) parts.push(`${canceled} cancelada(s)`);
          message = `Órdenes: ${parts.join(" y ")}.`;
        } else {
          message = "No había órdenes pendientes que separar o cancelar.";
        }

        this.$store.dispatch("successSnack", message);
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "No se pudieron separar las órdenes.");
      }
    },

    // ----- Catch-up (reposición) order for a re-included pet -----

    // The plan's active order cycle (the show endpoint only eager-loads active
    // cycles, but filter defensively). null when between periods.
    activeOrderCycle() {
      const cycles = (this.quote && this.quote.order_cycles) || [];
      return cycles.find((c) => c.status === "active") || null;
    },

    // True when an active pet has NO live (non-terminal) order in the active
    // cycle — i.e. its order was canceled on exclusion and never re-created.
    dietMissingOrderInActiveCycle(diet) {
      const cycle = this.activeOrderCycle();
      if (!cycle) return false;
      const done = ["delivered", "sent", "failed_to_send", "canceled"];
      const hasLiveOrder = (cycle.orders || []).some(
        (o) => o.diet_id === diet.id && !done.includes(o.status)
      );
      return !hasLiveOrder;
    },

    catchUpWeeks() {
      const cycle = this.activeOrderCycle();
      return cycle ? Number(cycle.cycle_duration) : 0;
    },

    // Locked per-diet price for the cycle, falling back to the diet's current
    // weekly price (mirrors the backend's locked_diet_prices fallback).
    catchUpUnitPrice(diet) {
      const cycle = this.activeOrderCycle();
      const locked = cycle && cycle.locked_diet_prices ? cycle.locked_diet_prices[diet.id] : null;
      return locked != null ? Number(locked) : Number(diet.weekly_price);
    },

    catchUpCost(diet) {
      return this.catchUpUnitPrice(diet) * this.catchUpWeeks();
    },

    openCatchUpModal(diet) {
      this.dietToCatchUp = diet;
      this.showCatchUpModal = true;
    },

    closeCatchUpModal() {
      this.showCatchUpModal = false;
      this.dietToCatchUp = null;
    },

    async confirmCatchUp() {
      const diet = this.dietToCatchUp;
      this.showCatchUpModal = false;
      if (!diet) return;

      try {
        const res = await api.quotes.generateDietCatchUpOrder(this.quote.id, diet.id);
        this.$store.dispatch("successSnack", res.message || "Orden de reposición creada.");
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "No se pudo generar la orden.");
      } finally {
        this.dietToCatchUp = null;
      }
    },

    formatDatetime,

    integerToCurrency,

    openDeliveryModeModal() {
      this.deliveryModeError = null;
      this.deliveryModeForm = {
        pickupChoice: this.quote.current_pickup_delivery ? 'true' : 'false',
        addressId: this.quote.address_id || null,
      };
      this.showDeliveryModeModal = true;
    },

    closeDeliveryModeModal() {
      if (this.changingDeliveryMode) return;
      this.showDeliveryModeModal = false;
      this.deliveryModeError = null;
    },

    async submitDeliveryModeChange() {
      this.deliveryModeError = null;
      const wantPickup = this.deliveryModeForm.pickupChoice === 'true';
      if (!wantPickup && !this.deliveryModeForm.addressId) {
        this.deliveryModeError = 'Selecciona una dirección de envío.';
        return;
      }
      this.changingDeliveryMode = true;
      try {
        await api.quotes.changeDeliveryMode(this.quote.id, {
          pickup_delivery: wantPickup,
          address_id: wantPickup ? null : this.deliveryModeForm.addressId,
        });
        this.showDeliveryModeModal = false;
        this.$store.dispatch('successSnack', 'Método de entrega actualizado.');
        await this.loadQuote();
      } catch (e) {
        this.deliveryModeError =
          e?.response?.data?.message || 'No fue posible cambiar el método de entrega.';
      } finally {
        this.changingDeliveryMode = false;
      }
    },

    async loadFrequencies() {
      this.loading = true;

      try {
        const response = await api.resources.getFrequencies();
        this.deliveryFrequencies = response.delivery_frequencies;
        this.paymentFrequencies = response.payment_frequencies;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async loadQuote() {
      this.loading = true;

      try {
        this.quote = await api.quotes.getById(this.$route.params.id);
        this.$store.commit('setBreadcrumbLabel', {
          routeName: this.$route.name,
          label: 'Plan #' + this.quote.id,
        });
        this.quote.diets.forEach((diet) => (diet.collapsed = true));
        this.currentBudget = this.quote.order_cycles[0]
          ? this.quote.order_cycles[0].current_budget
          : null;
        this.selectedAddressId = this.quote.address_id;

        // Auto-open the cash-renewal modal when arriving from the payment-issues list.
        if (
          this.$route.query.action === "cash-renewal" &&
          this.quote.is_cash_plan &&
          this.quote.status === "suspended"
        ) {
          this.openCashModal("renew");
        }
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: "quotes" });
      }

      this.loading = false;
    },

    getPaymentMethod(id) {
      const paymentMethod = this.paymentMethods.filter((p) => p.id === id)[0];

      return (
        "**** " +
        paymentMethod.card.last4 +
        " - " +
        paymentMethod.card.brand.toUpperCase() +
        " - " +
        paymentMethod.card.exp_month +
        "/" +
        paymentMethod.card.exp_year
      );
    },

    getStatus(status) {
      switch (status) {
        case "pending":
          return "Pendiente";
        case "in_process":
          return "En Proceso";
        case "ready_for_pickup":
          return "Lista para pick-up";
        case "ready_for_delivery":
          return "Lista para entrega";
        case "sent":
          return "Enviada";
        case "delivered":
          return "Entregada";
        case "failed_to_send":
          return "Fallida al enviar";
        default:
          break;
      }
    },

    validateIfCanRequest(orderCycle) {
      if (!orderCycle.order_requests) {
        return true;
      }

      return orderCycle.order_requests.every(
        (request) => request.status !== "pending"
      );
    },

    hasPendingRequest(orderCycle) {
      return (
        orderCycle.order_requests.filter(
          (request) => request.status === "pending"
        ).length > 0
      );
    },

    /**
     * CAMBIO 2026-04-23: inicia edición del saldo capturando el ciclo específico.
     * Antes el Vue solo operaba sobre order_cycles[0]; ahora respeta el ciclo clickeado.
     */
    startCurrentBudgetEdit(orderCycle) {
      this.currentBudgetCycleId = orderCycle.id;
      this.currentBudget = orderCycle.current_budget;
      this.currentBudgetReason = '';
      this.showCurrentBudgetForm = true;
    },

    cancelCurrentBudgetEdit() {
      this.showCurrentBudgetForm = false;
      this.currentBudget = null;
      this.currentBudgetReason = '';
      this.currentBudgetCycleId = null;
    },

    async updateCurrentBudget() {
      this.confirmCurrentBudgetUpdate = false;

      if (!this.currentBudgetReason || this.currentBudgetReason.length < 5) {
        this.$store.dispatch("errorSnack", "Debes indicar un motivo del cambio (mínimo 5 caracteres).");
        return;
      }

      this.loadingCurrentBudgetUpdate = true;

      try {
        // CAMBIO 2026-04-23: usamos el ciclo específico (no hardcoded order_cycles[0])
        // y pasamos motivo obligatorio. El backend valida ambos.
        const cycleId = this.currentBudgetCycleId || this.quote.order_cycles[0].id;
        const response = await api.quotes.updateCurrentBudget(
          cycleId,
          {
            current_budget: this.currentBudget,
            reason: this.currentBudgetReason,
          }
        );

        // Si hubo warnings, mostrarlas como mensaje informativo (no error)
        if (response.warnings && response.warnings.length > 0) {
          this.$store.dispatch("successSnack",
            `${response.message}. Advertencias registradas: ${response.warnings.join('; ')}`
          );
        } else {
          this.$store.dispatch("successSnack", response.message);
        }

        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error actualizando saldo");
      }

      this.loadingCurrentBudgetUpdate = false;
      this.showCurrentBudgetForm = false;
      this.currentBudgetReason = '';
      this.currentBudgetCycleId = null;
    },

    async requestOrder(orderCycle) {
      this.orderCycleToGenerateNewOrders = null;
      this.loading = true;

      try {
        const response = await api.quotes.requestOrder(orderCycle.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async toggleCourtesy() {
      this.confirmCourtesyToggle = false;
      this.togglingCourtesy = true;

      try {
        const response = await api.quotes.toggleCourtesy(this.quote.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.togglingCourtesy = false;
    },

    async activateCourtesyPlan() {
      if (this.courtesyForm.delivery_enabled && !this.courtesyForm.delivery_frequency_id) {
        this.$store.dispatch("errorSnack", "Selecciona una frecuencia de entrega.");
        return;
      }

      this.activatingCourtesy = true;

      try {
        const response = await api.quotes.activateCourtesyPlan(this.quote.id, this.courtesyForm);
        this.$store.dispatch("successSnack", response.message);
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.activatingCourtesy = false;
    },

    openCourtesySettingsForm() {
      this.courtesyForm.delivery_frequency_id = this.quote.delivery_frequency_id;
      this.courtesyForm.delivery_enabled = this.quote.delivery_enabled;
      this.showCourtesySettingsForm = true;
    },

    async updateCourtesySettings() {
      this.updatingCourtesySettings = true;

      try {
        const response = await api.quotes.updateCourtesySettings(this.quote.id, this.courtesyForm);
        this.$store.dispatch("successSnack", response.message);
        this.showCourtesySettingsForm = false;
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.updatingCourtesySettings = false;
    },

    async updateAddress() {
      this.confirmAddressUpdate = false;
      this.loadingAddressUpdate = true;

      try {
        const response = await api.quotes.updateAddress(this.quote.id, {
          address_id: this.selectedAddressId,
        });
        this.$store.dispatch("successSnack", response.message);
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingAddressUpdate = false;
      this.showAddressForm = false;
    },

    async deleteElement() {
      this.loadingDelete = true;

      try {
        const response = await api.clients.delete(this.quote.id);
        this.$store.dispatch("successSnack", response.message);
        this.closeDeleteModal();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingDelete = false;
    },

    async loadPaymentAttempts() {
      // If quote already has payment_attempts loaded, use them
      if (this.quote.payment_attempts) {
        this.paymentAttempts = this.quote.payment_attempts;
        this.showPaymentAttempts = true;
        return;
      }

      this.loadingPaymentAttempts = true;

      try {
        this.paymentAttempts = await api.paymentAttempts.getByQuoteId(this.quote.id);
        this.showPaymentAttempts = true;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingPaymentAttempts = false;
    },

    async attachCoupon() {
      if (!this.couponCodeInput) return;
      this.couponWorking = true;

      try {
        const response = await api.quotes.attachCoupon(
          this.quote.id,
          this.couponCodeInput.trim()
        );
        this.$store.dispatch("successSnack", response.message);
        this.couponCodeInput = "";
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.couponWorking = false;
    },

    async detachCoupon() {
      this.couponWorking = true;

      try {
        const response = await api.quotes.detachCoupon(this.quote.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.couponWorking = false;
    },

    openCashModal(mode) {
      this.cashModalMode = mode;
      this.showCashModal = true;
    },

    closeCashModal() {
      if (this.recordingCash) return;
      this.showCashModal = false;
    },

    async submitCashPayment(payload) {
      this.recordingCash = true;

      try {
        const response =
          this.cashModalMode === "renew"
            ? await api.quotes.renewCash(this.quote.id, payload)
            : await api.quotes.activateCash(this.quote.id, payload);

        // El pago quedó registrado, pero SIN el descuento del cupón (ya no
        // válido): la alerta pesa más que el snack de éxito (slot único).
        if (response.coupon_warning) {
          this.$store.dispatch("errorSnack", response.coupon_warning);
        } else {
          this.$store.dispatch("successSnack", response.message);
        }
        this.showCashModal = false;
        await this.loadQuote();
        if (this.paymentAttempts !== null) {
          this.loadPaymentAttempts();
        }
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.recordingCash = false;
    },

    openRetryPaymentModal() {
      this.confirmRetryPayment = true;
    },

    openChargeNextCycleModal() {
      this.confirmChargeNextCycle = true;
    },

    // Reuses the same backend flow as "Reintentar pago" (POST payment-attempts
    // .../retry -> handlePayment), which works for active plans too: charges the
    // card, creates the next cycle + orders, or errors if orders are unreleased.
    chargeNextCycle() {
      this.confirmChargeNextCycle = false;
      this.retryPayment();
    },

    async retryPayment() {
      this.confirmRetryPayment = false;
      this.retryingPayment = true;

      try {
        const response = await api.paymentAttempts.retryPayment(this.quote.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadQuote();
        if (this.paymentAttempts !== null) {
          this.loadPaymentAttempts();
        }
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.retryingPayment = false;
    },

    reactivateQuote() {
      this.confirmReactivate = true;
    },

    async confirmReactivateQuote() {
      this.confirmReactivate = false;
      this.reactivatingQuote = true;

      try {
        const response = await api.paymentAttempts.reactivateQuote(this.quote.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.reactivatingQuote = false;
    },

    openGeneratePendingOrdersModal(orderCycle) {
      this.orderCycleToGenerate = orderCycle;
      this.confirmGeneratePendingOrders = true;
    },

    async generatePendingOrders() {
      if (!this.orderCycleToGenerate) return;

      this.generatingPendingOrders = true;
      const count = this.orderCycleToGenerate.total_cycles - this.orderCycleToGenerate.cycle_count;

      try {
        // CAMBIO 2026-04-23: pasamos generatePendingOrdersDeductBudget (default true).
        const response = await api.orders.generateOrders(
          this.orderCycleToGenerate.id,
          count,
          this.generatePendingOrdersDeductBudget
        );
        this.$store.dispatch("successSnack", response.message);
        this.loadQuote();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.generatingPendingOrders = false;
      this.confirmGeneratePendingOrders = false;
      this.orderCycleToGenerate = null;
      // Reset toggle to default for next use
      this.generatePendingOrdersDeductBudget = true;
    },
  },
};
</script>